import { useContext, useState, createRef, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Fab,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Typography,
  Grid
} from '@mui/material';

import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Collections as CollectionsIcon,
} from '@mui/icons-material';

import { useLocalStorage } from 'hooks/use-local-storage'
import { Product, AuthContext, FABGallery } from 'components';
import {
  ShopifyIcon
} from 'components/Icons';
import { photo_url } from 'config';

import './gallery.css'


export const Modal = (props) => {
  const { items, onClose } = props;
  const [ open, setOpen ] = useLocalStorage('products-gallery-open', false);
  const [ itemsPerRow, setItemsPerRow ] = useLocalStorage('products-gallery-items-per-row', 6);
  const [ editableProduct, setEditableProduct ] = useState(false);
  const [ selectedItems, setSelectedItems ] = useLocalStorage('products-gallery-selected-items', []);
  const { accountId, userCreatedBrands, brands } = useContext(AuthContext);

  useEffect(() => {
    if (items.length > 0) {
      setSelectedItems(items.filter(i => selectedItems.indexOf(i.id) !== -1).map(i => i.id))
    }
  }, [items])

  const handleSelect = (item) => {
    // Check if the item is already selected
    const index = selectedItems.indexOf(item.id);
    if (index === -1) {
      // If not selected, add it to the selectedItems array
      setSelectedItems([...selectedItems, item.id]);
    } else {
      // If already selected, remove it from the selectedItems array
      const updatedItems = [...selectedItems];
      updatedItems.splice(index, 1);
      setSelectedItems(updatedItems);
    }
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setTimeout(() => {
      setSelectedItems([]);
      setOpen(false);
      onClose();
    }, 10)
  }

  return (
    <Fab disableRipple color="secondary" aria-label="add" variant="extended" onClick={handleOpen}>
      <CollectionsIcon />
      View Gallery
      <Dialog fullScreen open={open} onClose={handleClose}>
        <DialogActions>
          <Grid sx={{marginTop: 2}} justifyContent="center" spacing={2} container>
            <Grid item>
              <Typography gutterBottom>
                Items per row
              </Typography>
            </Grid>
            <Grid item sm={2}>
              <Slider
                value={typeof itemsPerRow === 'number' ? itemsPerRow : 0}
                onChange={(event) => setItemsPerRow(event.target.value === '' ? 0 : Number(event.target.value))}
                aria-labelledby="input-slider"
                valueLabelDisplay="on"
                marks
                min={2}
                max={16}
              />
            </Grid>
          </Grid>
          { selectedItems.length !== items.length ? (<Button sx={{ minWidth: 145 }} variant="contained" onClick={() => { setSelectedItems(items.map(e => e.id)) }}>Select all</Button>) : null }
          { selectedItems.length > 0 ? (<Button sx={{ minWidth: 145 }} variant="contained" onClick={() => { setSelectedItems([]) }}>Clear selection</Button>) : null }
          <Button variant="contained" onClick={handleClose}>Close</Button>
        </DialogActions>
        <DialogContent>
          <ImageList
            sx={{
              padding: '0px 20px',
              // Promote the list into its own layer in Chrome. This costs memory, but helps keeping high FPS.
              transform: 'translateZ(0)',
              marginBottom: 20
            }}
            cols={itemsPerRow}
            rowHeight={'auto'}
            gap={5}
          >
            {items.map((item) => {
              // If no image, don't return any
              if (!item.photos) return false

              const selected = selectedItems.indexOf(item.id) !== -1

              return (
                <ImageListItem className={selected ? 'selected' : ''} key={item.id}>
                  {selected ? (
                    <div className="selected">
                      <CheckCircleIcon sx={{ float: 'right' }} />
                    </div>
                  ) : null}
                  <img
                    src={`${photo_url}/o/${accountId}%2F${item.photos[0]}.jpg?alt=media`}
                    alt={""}
                    loading="lazy"
                  />
                  <ImageListItemBar
                    sx={{
                      alignItems: 'flex-end',
                      height: '100%',
                      padding: '0 10px',
                      background:
                        'linear-gradient(to top, rgba(0,0,0,0.7) 0%, ' +
                        'rgba(0,0,0,0.3) 20%, rgba(0,0,0,0) 100%)',
                    }}
                    onClick={() => { handleSelect(item) }}
                    onDoubleClickCapture={() => setEditableProduct(item)}
                    title={
                      <Grid container>
                        <Grid item sm={12}>
                          <Typography sx={{ cursor: 'pointer' }} onClick={() => setEditableProduct(item)} variant="caption">{item.styleNumber}</Typography>
                        </Grid>
                        <Grid item sm={8} >
                          <span style={{ cursor: 'pointer' }} onClick={() => setEditableProduct(item)}>{(userCreatedBrands?.[item.brand] && userCreatedBrands?.[item.brand].displayName) || (brands?.[item.brand] && brands?.[item.brand].displayName) || ""}</span>
                        </Grid>
                        <Grid
                          item
                          sm={4}
                          sx={{ 
                            position: 'absolute',
                            textAlign: 'right',
                            bottom: '5px',
                            right: '10px'
                          }}
                        >
                          {item?.integrations?.shopify?.product_id || item?.integrations?.shopify_v2 ? (
                            <IconButton color='secondary'>
                              <ShopifyIcon />
                            </IconButton>
                          ) : null}
                          {item.deliveries ? (
                            <IconButton color='secondary'>
                              <CheckCircleIcon />
                            </IconButton>
                          ) : null}
                        </Grid>
                      </Grid>
                    }
                    position="bottom"
                    actionPosition="left"
                  />
                </ImageListItem>
              );
            })}
          </ImageList>
        </DialogContent>
        {editableProduct && (
          <Product
            product={editableProduct} 
            open={editableProduct ? true : false}
            onClose={() => {
              setEditableProduct(false)
            }}
            button={(
              <IconButton sx={{ color: 'white', display: 'none' }}>
                <EditIcon />
              </IconButton>
            )} 
          />
        )}
        <FABGallery createProduct={() => { setEditableProduct({ create: true, orderStatus: 1 }) }} isFullScreen={true} selected={selectedItems.length > 0} items={items.filter((i) => selectedItems.indexOf(i.id) !== -1)} />
      </Dialog>
    </Fab>
  )
}